// Keeps an open calendar view in step with appointment changes made by other users, off the back of
// the PushEventService poll. Two people with the same calendar open see each other's edits appear
// without a refresh.
//
// The point of this service is that it patches a single row rather than re-fetching the view. A
// calendar range holds hundreds of appointments; an edit changes one. So an event is first tested
// against what the view is actually showing (its date range, clinician filter and practice) and
// dropped if it isn't on screen, and only then is that one appointment fetched by id. A delete needs
// no fetch at all. An event for an appointment outside the view costs zero requests.
//
// A view subscribes with its own scope, so the listeners die with the calendar modal:
//
//   AppointmentLiveUpdateService.subscribe($scope, {
//       appointments: function () { return $scope.allAppointments; },
//       isInView: function (change) { return ...; },
//       onChanged: function () { renderAppointmentEvents($scope.allAppointments); },
//       onPatched: function (appointment) { ... },   // optional
//       onRemoved: function (appointmentId) { ... }, // optional
//   });
app.service("AppointmentLiveUpdateService", [
	"AppointmentAPI",
	"Session",
	"PUSH_EVENTS",
	"LiveUpdateGuard",
	function (AppointmentAPI, Session, PUSH_EVENTS, LiveUpdateGuard) {
		/**
		 * Wires a calendar view up to appointment push events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  appointments() - the raw appointment list, patched in place
		 *              isInView(change) - whether that appointment is within what the view is showing.
		 *                  Called with {appointmentId, patientId, clinicianId, startDateUTC}. This is
		 *                  what saves the request, so it should be as tight as the view's own query.
		 *              onChanged() - re-render from the patched list
		 *              onPatched(appointment) - optional; called after onChanged() with the fetched
		 *                  appointment that was put into the list. For a view that holds one appointment
		 *                  open on its own (a detail dialog) and has to refresh that copy too.
		 *              onRemoved(appointmentId) - optional; called after onChanged() when an appointment
		 *                  was taken out of the list, whether it was deleted or moved out of view. The
		 *                  same dialog has to close, because there is nothing left to show or save.
		 */
		function subscribe(scope, spec) {
			// Appointment ids with a fetch in flight. A second event for the same appointment must not
			// race the first: two responses can land out of order and leave the older one displayed.
			const fetching = {};

			/**
			 * The view's appointment list, or null before its first load has landed. A view with no list
			 * yet has nothing to patch, and its own first fetch will include the change anyway.
			 */
			function appointments() {
				const list = spec.appointments();
				return angular.isArray(list) ? list : null;
			}

			/** Tells the view an appointment is no longer in its list, if it asked to be told. */
			function notifyRemoved(appointmentId) {
				if (angular.isFunction(spec.onRemoved)) {
					spec.onRemoved(appointmentId);
				}
			}

			/** Tells the view which appointment was just put into its list, if it asked to be told. */
			function notifyPatched(appointment) {
				if (angular.isFunction(spec.onPatched)) {
					spec.onPatched(appointment);
				}
			}

			/**
			 * Whether two appointment ids name the same appointment. Compared as text, the way this
			 * service already compares clinician ids: an id reaches us from the event payload and from
			 * the appointment list by different routes, and a strict comparison that disagreed on type
			 * would report the appointment as absent - which is how a duplicate row gets added.
			 */
			function sameId(a, b) {
				return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
			}

			/** The index of an appointment in the given list, or -1. */
			function indexOf(list, appointmentId) {
				for (let i = 0; i < list.length; i++) {
					if (sameId(list[i].patient_appointment_id, appointmentId)) {
						return i;
					}
				}
				return -1;
			}

			/**
			 * Drops every copy of an appointment from the view. Every copy, not the first: if a duplicate
			 * ever did get in, the next event for that appointment has to be able to clear it, or it sits
			 * on the calendar until the whole view is re-fetched.
			 *
			 * @return whether anything was actually removed
			 */
			function remove(list, appointmentId) {
				let removed = false;
				for (let i = list.length - 1; i >= 0; i--) {
					if (sameId(list[i].patient_appointment_id, appointmentId)) {
						list.splice(i, 1);
						removed = true;
					}
				}
				return removed;
			}

			/**
			 * Puts the fetched appointment into the list exactly once, where any existing copy sat.
			 *
			 * Written as remove-every-copy-then-insert rather than an assignment at an index so that the
			 * patch is idempotent - applying it to a list that somehow already holds two copies leaves
			 * one, instead of leaving two and adding a third.
			 *
			 * getAppointment can return a thinner row than the list query does: the query endpoint adds the
			 * practice name afterwards, and when Xero could not be asked the invoice summaries come back
			 * empty and flagged invoices_unavailable. Assigning the fetched row straight over the existing
			 * one would then silently drop the invoice icon and its hover detail, so the fields the fetch
			 * did not supply are carried across from the row being replaced. An appointment edit does not
			 * change its invoices; those arrive as their own INVOICE_* events.
			 */
			function put(list, appointment) {
				const appointmentId = appointment.patient_appointment_id;
				const existing = list[indexOf(list, appointmentId)];
				const patched = angular.extend({}, appointment);

				if (existing) {
					if (patched.invoice_list === undefined || patched.invoices_unavailable === true) {
						patched.invoice_list = existing.invoice_list;
					}
					if (patched.practice_name === undefined) {
						patched.practice_name = existing.practice_name;
					}
				}

				let at = indexOf(list, appointmentId);
				remove(list, appointmentId);
				if (at === -1 || at > list.length) {
					at = list.length;
				}
				list.splice(at, 0, patched);
			}

			/**
			 * Fetches the one changed appointment and patches it into the list - replacing the row when
			 * the view already holds it, appending when the change brought it into view.
			 */
			function fetchAndPatch(change) {
				if (fetching[change.appointmentId]) {
					// A fetch is already on its way for this appointment. It was issued after this change
					// was published, so it returns this change's state (or newer) and there is nothing to
					// add by asking again.
					return;
				}
				fetching[change.appointmentId] = true;

				// The event came from the practice channel we poll, so the appointment belongs to the
				// session practice - not necessarily the one the calendar is currently displaying.
				// Through LiveUpdateGuard, which stops this after a few failures in a row.
				LiveUpdateGuard.run("appointment", function () {
					return AppointmentAPI.getAppointment(change.patientId, change.appointmentId, Session.practice.id);
				}).then(
					function (resp) {
						delete fetching[change.appointmentId];

						const appointment = resp.data;
						if (!appointment || !appointment.patient_appointment_id) {
							return;
						}

						// Re-read the list: the view may have re-fetched while this was in flight.
						const list = appointments();
						if (!list) {
							return;
						}

						// Judge the appointment we actually got back, not the event that announced it. The
						// two can disagree - a second edit while this was in flight - and inserting a row
						// the view should not be showing puts an appointment on the wrong day or clinician.
						if (!spec.isInView(toChangeFromAppointment(appointment))) {
							if (remove(list, change.appointmentId)) {
								spec.onChanged();
								notifyRemoved(change.appointmentId);
							}
							return;
						}

						put(list, appointment);
						spec.onChanged();
						notifyPatched(appointment);
					},
					function () {
						// Deliberately silent: the poll will not re-announce this change, so the view stays
						// as it is until the next one. Nothing is logged - the response carries patient data.
						delete fetching[change.appointmentId];
					}
				);
			}

			/** Applies one added/updated event to the view. */
			function onUpsert(pushEvent) {
				const change = toChange(pushEvent);
				const list = appointments();
				if (!list || !change.appointmentId || !change.patientId) {
					return;
				}

				if (!spec.isInView(change)) {
					// Not on screen. Either it never was - ignore it without spending a request - or it has
					// just been moved or reassigned out of the view, in which case it has to disappear.
					if (remove(list, change.appointmentId)) {
						spec.onChanged();
						notifyRemoved(change.appointmentId);
					}
					return;
				}

				fetchAndPatch(change);
			}

			/** Applies one deleted event. No fetch - there is nothing left to fetch. */
			function onDelete(pushEvent) {
				const change = toChange(pushEvent);
				const list = appointments();
				if (list && change.appointmentId && remove(list, change.appointmentId)) {
					spec.onChanged();
					notifyRemoved(change.appointmentId);
				}
			}

			scope.$on(PUSH_EVENTS.APPOINTMENT_ADDED, function (e, pushEvent) {
				onUpsert(pushEvent);
			});
			scope.$on(PUSH_EVENTS.APPOINTMENT_UPDATED, function (e, pushEvent) {
				onUpsert(pushEvent);
			});
			scope.$on(PUSH_EVENTS.APPOINTMENT_DELETED, function (e, pushEvent) {
				onDelete(pushEvent);
			});
		}

		/** The event's payload, under the names the rest of this service uses. */
		function toChange(pushEvent) {
			const data = (pushEvent && pushEvent.data) || {};
			return {
				appointmentId: data.patientAppointmentId,
				patientId: data.patientId,
				clinicianId: data.clinicianId,
				startDateUTC: data.startDateUTC,
			};
		}

		/** A fetched appointment, under those same names, so it can be put through the same isInView. */
		function toChangeFromAppointment(appointment) {
			return {
				appointmentId: appointment.patient_appointment_id,
				patientId: appointment.patient_id,
				clinicianId: appointment.clinician_id,
				startDateUTC: appointment.start_date_utc,
			};
		}

		const service = {
			subscribe: subscribe,

			/**
			 * Whether a change falls inside a calendar's fetched window and clinician filter - the same
			 * two conditions the view's own queryPatientAppointment call uses, so the view holds exactly
			 * the appointments this returns true for.
			 *
			 * @param change      the change, from the event payload
			 * @param rangeStart  the calendar's range start, UTC ISO
			 * @param rangeEnd    the calendar's range end, UTC ISO
			 * @param clinicianId the clinician being shown, or a falsy value for all of them
			 */
			isWithinCalendarWindow: function (change, rangeStart, rangeEnd, clinicianId) {
				if (!rangeStart || !rangeEnd || !change.startDateUTC) {
					return false;
				}

				// Compare as instants. The strings are all UTC ISO but not necessarily to the same
				// precision, so comparing them as text would be wrong at the edges.
				const start = moment.utc(change.startDateUTC);
				if (!start.isValid() || start.isBefore(moment.utc(rangeStart)) || start.isAfter(moment.utc(rangeEnd))) {
					return false;
				}

				// Ids cross the wire as numbers but reach the filters as either, so compare loosely.
				if (clinicianId && String(clinicianId) !== String(change.clinicianId)) {
					return false;
				}

				return true;
			},
		};

		return service;
	},
]);
